import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * iter90 — Carrier picker used on BoL / Packing Slip forms.
 *
 * Props:
 *   value: current carrier *name* string
 *   onChange(name): when the user types or picks a new carrier
 *   onPickRich(carrier): called with the full carrier doc when a saved one is picked,
 *       so the parent form can auto-fill SCAC + default_freight_terms.
 *   disabled: lock input
 *
 * UX: shows a Select of saved carriers; selecting "— type custom —" switches to a
 * free-text Input so users can still enter one-off carriers.
 */
const CarrierSelect = ({ value, onChange, onPickRich, disabled, testid }) => {
  const { api } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [custom, setCustom] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api().get('/carriers'); setCarriers(r.data || []); }
      catch { /* non-blocking */ }
    })();
  }, [api]);

  // If current value doesn't match any saved carrier, default to custom-mode so the user sees their text.
  useEffect(() => {
    if (value && carriers.length && !carriers.find(c => c.name === value)) setCustom(true);
  }, [value, carriers]);

  if (custom) {
    return (
      <div className="flex gap-1">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Type a carrier name"
          className="rounded-sm"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setCustom(false)}
          disabled={disabled}
          className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-sm text-slate-600 whitespace-nowrap"
          data-testid={testid ? `${testid}-pick-saved` : undefined}
          aria-label="Pick a saved carrier instead"
        >Use saved</button>
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      <Select
        value={value || ''}
        onValueChange={(v) => {
          if (v === '__custom__') { setCustom(true); onChange(''); return; }
          const c = carriers.find(x => x.name === v);
          onChange(v);
          if (c && onPickRich) {
            onPickRich(c);
            toast.success(`${c.name} loaded — SCAC & terms auto-filled`);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger className="rounded-sm" data-testid={testid}>
          <SelectValue placeholder={carriers.length ? "Pick a carrier…" : "No carriers saved yet"} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {carriers.map(c => (
            <SelectItem key={c.id} value={c.name}>
              {c.name}{c.scac ? ` (${c.scac})` : ''}
            </SelectItem>
          ))}
          <SelectItem value="__custom__">— type custom —</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export default CarrierSelect;
